/**
 * Text tokenization utilities for streaming animation.
 *
 * Tokens are the atomic units of the animation - each token is revealed
 * individually with its own timing. The tokenizer splits text into words
 * and newlines, preserving the structure needed for natural reading flow.
 */
import { StreamingToken } from './types';

/**
 * Tokenizes text into words and newlines for streaming animation.
 *
 * ## Tokenization Strategy
 *
 * 1. Split by newlines first (preserving them as separate tokens)
 * 2. Within each line, split by whitespace to get words
 * 3. Multiple spaces collapse (treated same as single space)
 * 4. Punctuation stays attached to words (e.g., "Hello," is one token)
 *
 * ## Why Newlines Are Separate Tokens
 *
 * Newlines need special handling in the animation:
 * - They render as `<br>` elements, not text
 * - They have different timing (longer pause)
 * - They affect the delay calculation for the following word
 *
 * @example
 * tokenizeText("Hello world")
 * // => [{ content: "Hello", ... }, { content: "world", ... }]
 *
 * tokenizeText("Hello\nworld")
 * // => [{ content: "Hello", ... }, { content: "\n", isNewline: true }, { content: "world", ... }]
 *
 * @param text - The text to tokenize
 * @param idPrefix - Optional prefix for token IDs (use React's useId() to avoid ID clashes between instances)
 */
export function tokenizeText(
    text: string,
    idPrefix?: string
): StreamingToken[] {
    const tokens: StreamingToken[] = [];
    const prefix = idPrefix ?? 'token';
    let id = 0;

    // Split by newlines first, preserving them
    const segments = text.split(/(\n)/);

    for (const segment of segments) {
        if (segment === '\n') {
            tokens.push({
                id: `${prefix}-${id++}`,
                content: '\n',
                isNewline: true,
                isVisible: false,
                index: tokens.length,
            });
        } else if (segment.length > 0) {
            // Split segment into words (non-whitespace sequences)
            const words = segment.match(/\S+/g) || [];
            for (const word of words) {
                tokens.push({
                    id: `${prefix}-${id++}`,
                    content: word,
                    isNewline: false,
                    isVisible: false,
                    index: tokens.length,
                });
            }
        }
    }

    return tokens;
}

/**
 * Reconstructs visible text from tokens for accessibility/screen readers.
 *
 * This is used for the `visibleText` return value, which can be used for
 * screen reader announcements or debugging.
 *
 * ## Why the Regex Cleanup?
 *
 * Tokens are joined with spaces, but newline tokens shouldn't have spaces
 * around them. The regex replacements clean up these cases:
 * - " \n " → "\n" (space-newline-space)
 * - "\n " → "\n" (newline-space)
 * - " \n" → "\n" (space-newline)
 *
 * @example
 * // Tokens: ["Hello", "\n", "world"] (all visible)
 * // Join with space: "Hello \n world"
 * // After cleanup: "Hello\nworld"
 */
export function getVisibleText(tokens: StreamingToken[]): string {
    return tokens
        .filter((t) => t.isVisible)
        .map((t) => (t.isNewline ? '\n' : t.content))
        .join(' ')
        .replace(/ \n /g, '\n')
        .replace(/\n /g, '\n')
        .replace(/ \n/g, '\n');
}
